import { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuthStore } from "@/stores/authStore";
import { useGroupStore } from "@/stores/groupStore";
import {
  LayoutDashboard,
  HandCoins,
  TrendingUp,
  BarChart3,
  Users,
  Settings,
  Bell,
  Menu,
  X,
  ChevronDown,
  Plus,
  LogOut,
  KeyRound,
  Mail,
  UserCircle,
  Wallet,
  FileSpreadsheet,
  ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CreateGroupDialog } from "@/components/groups/CreateGroupDialog";
import { JoinGroupDialog } from "@/components/groups/JoinGroupDialog";
import { MyInvitesDialog } from "@/components/invitations/MyInvitesDialog";
import { useMyInvitations } from "@/hooks/useMyInvitations";
import { useQueryClient } from "@tanstack/react-query";
import { useGroupAdmin } from "@/hooks/useGroupAdmin";
import { getCurrentUserRole } from "@/lib/groupRoles";


interface NavLink {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  isAdminOnly?: boolean;
  requiresGroup?: boolean;
}

const navLinks: NavLink[] = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { name: "Members", path: "/members", icon: Users, requiresGroup: true },
  { name: "Loans", path: "/loans", icon: HandCoins, requiresGroup: true },
  { name: "Investments", path: "/investments", icon: TrendingUp, requiresGroup: true },
  { name: "Wallet", path: "/wallet", icon: Wallet, requiresGroup: true },
  { name: "Reports", path: "/reports", icon: BarChart3, requiresGroup: true },
  { name: "Settings", path: "/settings", icon: Settings, requiresGroup: true },
];

const manageLinks = [
  { name: "Bulk Transactions", path: "/bulk-transactions", icon: FileSpreadsheet },
  { name: "Audit Log", path: "/audit-log", icon: ClipboardList },
];

export function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isAdmin = useGroupAdmin();
  const { groups, activeGroup, setActiveGroup } = useGroupStore();
  const queryClient = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [joinGroupOpen, setJoinGroupOpen] = useState(false);
  const [myInvitesOpen, setMyInvitesOpen] = useState(false);

  const { invitations: myPendingInvites } = useMyInvitations();

  const userInitials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`
    : "??";
  const userFullName = user ? `${user.firstName} ${user.lastName}` : "User";
  const userEmail = user?.email ?? "";

  const handleGroupSwitch = (group: typeof activeGroup) => {
    if (activeGroup?.uuid !== group.uuid) {
      queryClient.clear();
    }
    setActiveGroup(group);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const activeRole = useMemo(
    () => (activeGroup ? getCurrentUserRole(activeGroup.userRoles, user?.id)?.role ?? null : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeGroup?.uuid, activeGroup?.userRoles, user?.id]
  );

  return (
    <header className="sticky top-0 z-50 w-full bg-primary">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo & Group Switcher */}
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo.png" alt="KashRound" width={48} className="w-32" />
          </Link>

          {/* Group Switcher */}
          {groups.length > 0 && activeGroup ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="hidden h-9 gap-2 rounded-lg bg-primary-foreground/10 px-3 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground md:flex"
                >
                  <span className="max-w-[150px] truncate text-sm font-medium">
                    {activeGroup.name}
                  </span>
                  {activeRole && (
                    <Badge variant="secondary" className="h-5 bg-secondary/80 text-[10px] font-medium text-secondary-foreground">
                      {activeRole}
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 animate-slide-down">
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  Your Groups
                </DropdownMenuLabel>
                {groups.map((group) => {
                  const role = getCurrentUserRole(group.userRoles, user?.id)?.role ?? null;
                  return (
                    <DropdownMenuItem
                      key={group.uuid}
                      onClick={() => handleGroupSwitch(group)}
                      className={cn(
                        "flex cursor-pointer flex-col items-start gap-1 rounded-lg p-3",
                        activeGroup.uuid === group.uuid && "bg-muted"
                      )}
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="font-medium">{group.name}</span>
                        {role && (
                          <Badge variant={role.toLowerCase().includes("chair") ? "default" : "secondary"} className="text-[10px]">
                            {role}
                          </Badge>
                        )}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 text-accent cursor-pointer"
                  onClick={() => setCreateGroupOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Create New Group
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2 text-secondary cursor-pointer"
                  onClick={() => setJoinGroupOpen(true)}
                >
                  <KeyRound className="h-4 w-4" />
                  Join with Code
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              className="hidden h-9 gap-2 rounded-lg bg-primary-foreground/10 px-3 text-sm text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground md:flex"
              onClick={() => setCreateGroupOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Join or Create a Group
            </Button>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden xl:flex xl:items-center xl:gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            const isDisabled = link.requiresGroup && groups.length === 0;
            if (isDisabled) {
              return (
                <span
                  key={link.path}
                  className="nav-link cursor-not-allowed opacity-40 select-none text-primary-foreground/70"
                >
                  {link.name}
                </span>
              );
            }
            return (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "nav-link text-primary-foreground/70 hover:text-primary-foreground",
                  isActive && "active text-accent"
                )}
              >
                {link.name}
              </Link>
            );
          })}

          {isAdmin && !!activeGroup && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "nav-link flex items-center gap-1 text-primary-foreground/70 hover:text-primary-foreground",
                    manageLinks.some((l) => location.pathname === l.path) && "active text-accent"
                  )}
                >
                  Manage
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="animate-slide-down">
                {manageLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link
                        to={link.path}
                        className={cn(
                          "flex cursor-pointer items-center gap-2",
                          location.pathname === link.path && "text-accent"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {link.name}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="relative text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          >
            <Bell className="h-5 w-5" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-accent animate-pulse-glow" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="gap-2 px-2 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              >
                <Avatar className="h-8 w-8 border-2 border-accent/50">
                  <AvatarImage src="" />
                  <AvatarFallback className="bg-secondary text-sm font-medium text-secondary-foreground">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="hidden h-4 w-4 sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 animate-slide-down">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium">{userFullName}</span>
                  <span className="text-xs font-normal text-muted-foreground">{userEmail}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => navigate("/profile")}>
                <UserCircle className="h-4 w-4" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => setMyInvitesOpen(true)}>
                <Mail className="h-4 w-4" />
                Pending Invites
                {myPendingInvites.length > 0 && (
                  <Badge variant="secondary" className="ml-auto h-5 text-[10px]">
                    {myPendingInvites.length}
                  </Badge>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="text-primary-foreground hover:bg-primary-foreground/10 xl:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="absolute left-0 top-16 w-full animate-slide-down border-t border-primary-foreground/10 bg-primary xl:hidden">
          {/* Mobile Group Switcher */}
          <div className="border-b border-primary-foreground/10 p-4">
            <p className="mb-2 text-xs font-medium text-primary-foreground/50">Current Group</p>
            {groups.length > 0 && activeGroup ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full justify-between bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate">{activeGroup.name}</span>
                      {activeRole && (
                        <Badge variant="secondary" className="bg-secondary/80 text-[10px]">
                          {activeRole}
                        </Badge>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[calc(100vw-2rem)]">
                  {groups.map((group) => {
                    const role = getCurrentUserRole(group.userRoles, user?.id)?.role ?? null;
                    return (
                      <DropdownMenuItem
                        key={group.uuid}
                        onClick={() => handleGroupSwitch(group)}
                        className="flex flex-col items-start gap-1 p-3"
                      >
                        <div className="flex w-full items-center justify-between">
                          <span className="font-medium">{group.name}</span>
                          {role && (
                            <Badge variant={role.toLowerCase().includes("chair") ? "default" : "secondary"} className="text-[10px]">
                              {role}
                            </Badge>
                          )}
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                className="w-full justify-start bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={() => setCreateGroupOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Join or Create a Group
              </Button>
            )}
          </div>

          {/* Mobile Nav Links */}
          <nav className="flex flex-col p-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              const Icon = link.icon;
              const isDisabled = link.requiresGroup && groups.length === 0;
              if (isDisabled) {
                return (
                  <span
                    key={link.path}
                    className="flex items-center gap-3 rounded-lg px-4 py-3 cursor-not-allowed opacity-40 select-none text-primary-foreground/70"
                  >
                    <Icon className="h-5 w-5" />
                    {link.name}
                  </span>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-4 py-3 text-primary-foreground/70 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground",
                    isActive && "bg-primary-foreground/10 text-accent"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {link.name}
                </Link>
              );
            })}

            {isAdmin && !!activeGroup && (
              <>
                <div className="mt-1 border-t border-primary-foreground/10 px-4 pb-1 pt-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-primary-foreground/40">
                    Manage
                  </span>
                </div>
                {manageLinks.map((link) => {
                  const isActive = location.pathname === link.path;
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-4 py-3 text-primary-foreground/70 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground",
                        isActive && "bg-primary-foreground/10 text-accent"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {link.name}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      )}

      {/* Group Dialogs */}
      <CreateGroupDialog open={createGroupOpen} onOpenChange={setCreateGroupOpen} />
      <JoinGroupDialog open={joinGroupOpen} onOpenChange={setJoinGroupOpen} />
      <MyInvitesDialog open={myInvitesOpen} onOpenChange={setMyInvitesOpen} />
    </header>
  );
}
